/**
 * 蜜糖黄 · 阳光黄页 VIP 页
 *
 * 业务同共享 VIP 页：getVipPlans 映射套餐 + getSiteSettings 控制开关 + 自定义 fetchPaymentConfig。
 * 视觉：炭黑 Hero + 琥珀权益卡 + 套餐角标；无收款码时展示占位提示，不伪造支付入口。
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getVipPlans, getSiteSettings } from "@/utils/adminApi";
import HoneyNavbar from "@/templates/honey/components/Navbar";
import HoneyFooter from "@/templates/honey/components/Footer";

// ==================== API 层 ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem("current_site_id") || "";
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "X-Site-Id": siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("honey VIP: 读取支付配置失败", e);
    return null;
  }
}

// ==================== 类型与常量 ====================

interface VipPlan {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  duration: string;
  badge: string;
  features: string[];
}

const DEFAULT_VIP_PLANS: VipPlan[] = [
  {
    id: "monthly",
    name: "月度会员",
    price: 29,
    originalPrice: 49,
    duration: "1个月",
    badge: "",
    features: ["信息置顶展示 3次/月", "发布信息不限条数", "专属会员标识", "优先客服支持"],
  },
  {
    id: "quarterly",
    name: "季度会员",
    price: 79,
    originalPrice: 147,
    duration: "3个月",
    badge: "热门",
    features: ["信息置顶展示 10次/季", "发布信息不限条数", "专属会员标识", "优先客服支持", "数据统计分析"],
  },
  {
    id: "yearly",
    name: "年度会员",
    price: 199,
    originalPrice: 588,
    duration: "12个月",
    badge: "超值",
    features: [
      "信息置顶展示 不限次数",
      "发布信息不限条数",
      "专属会员标识",
      "专属客服支持",
      "数据统计分析",
      "优先审核通道",
    ],
  },
];

const VIP_BENEFITS = [
  { icon: "ri-rocket-line", title: "置顶曝光", desc: "信息优先展示在列表顶部，获得更多浏览量" },
  { icon: "ri-shield-check-line", title: "认证标识", desc: "专属VIP认证标识，提升可信度" },
  { icon: "ri-bar-chart-line", title: "数据分析", desc: "查看信息浏览、联系等详细数据统计" },
  { icon: "ri-customer-service-line", title: "专属客服", desc: "优先响应，专属客服一对一服务" },
  { icon: "ri-infinity-line", title: "无限发布", desc: "不限条数发布信息，无需担心限制" },
  { icon: "ri-speed-line", title: "极速审核", desc: "信息优先审核，快速上线展示" },
];

export default function HoneyVipPage() {
  const { isLoggedIn } = useAuth();
  const [vipPlans, setVipPlans] = useState<VipPlan[]>(DEFAULT_VIP_PLANS);
  const [selectedPlan, setSelectedPlan] = useState("quarterly");
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [enableVip, setEnableVip] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [plans, settings, payments] = await Promise.all([
          getVipPlans().catch(() => null),
          getSiteSettings().catch(() => null),
          fetchPaymentConfig(),
        ]);
        if (settings) setEnableVip(settings.enable_vip !== false);
        if (plans && Array.isArray(plans) && plans.length > 0) {
          const formatted = plans.map((p: any) => ({
            id: p.id,
            name: p.name || "VIP套餐",
            price: p.amount || p.price || 0,
            originalPrice: p.original_price || p.originalPrice || 0,
            duration: `${p.duration_months || p.duration || 1}个月`,
            badge: p.is_recommended ? "推荐" : "",
            features: p.benefits || p.features || [],
          }));
          setVipPlans(formatted);
          if (formatted.length > 0) setSelectedPlan(formatted[0].id);
        }
        if (payments) {
          setPaymentConfig(payments);
          if (payments.qrcode_enabled && payments.qrcode_priority) setPayMethod("qrcode");
        }
      } catch (e) {
        console.error("加载VIP数据失败:", e);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const currentPlan = vipPlans.find((p) => p.id === selectedPlan);

  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);
  const showQrcode = showAlipay && showWechat;

  useEffect(() => {
    if (payMethod === "wechat" && !showWechat) setPayMethod(showAlipay ? "alipay" : "qrcode");
    else if (payMethod === "alipay" && !showAlipay) setPayMethod(showWechat ? "wechat" : "qrcode");
    else if (payMethod === "qrcode" && !showQrcode) setPayMethod(showWechat ? "wechat" : "alipay");
  }, [showWechat, showAlipay, showQrcode, payMethod]);

  const handlePay = () => {
    if (!isLoggedIn) {
      window.location.href = "/login?redirect=/vip";
      return;
    }
    setShowPayModal(true);
  };

  const payButtonBase =
    "flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm transition-all cursor-pointer";
  const payButtonActive = (active: boolean, hex: string) =>
    active
      ? `border-[#F0A500] bg-[#FFF3D6] text-[#B87A00] shadow-sm`
      : "border-[#EAE1CB] bg-white text-[#6E6658] hover:border-[#F0A500]/60";

  return (
    <div className="min-h-screen bg-[#FFF9EC]">
      <HoneyNavbar />
      <div className="lg:pl-60">
        {/* VIP 功能关闭提示 */}
        {!enableVip && !loading && (
          <section className="max-w-3xl mx-auto px-4 py-24 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-[#FFF3D6]">
              <i className="ri-vip-crown-line text-4xl text-[#B87A00]" />
            </div>
            <h1 className="text-2xl font-black text-[#23201A]">VIP会员功能已关闭</h1>
            <p className="mt-2 text-sm text-[#8B8577]">非常抱歉，VIP会员功能目前暂时关闭，如有需要请联系管理员。</p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 rounded-xl bg-[#23201A] text-[#F0A500] text-sm font-bold hover:bg-[#3A3529] transition-colors"
            >
              <i className="ri-arrow-left-line" />返回会员中心
            </Link>
          </section>
        )}

        {enableVip && (
          <>
            {/* Hero */}
            <section className="relative overflow-hidden bg-[#23201A]">
              <div className="pointer-events-none absolute -top-16 right-10 w-64 h-64 rounded-full border-[18px] border-[#F0A500]/10" />
              <div className="pointer-events-none absolute -bottom-20 -left-16 w-72 h-72 rounded-full border-[24px] border-[#F0A500]/5" />
              <div className="relative max-w-5xl mx-auto px-4 lg:px-8 py-12 lg:py-16 text-center">
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#F0A500] text-[#23201A] text-xs font-black">
                  <i className="ri-vip-crown-2-line" />VIP 会员
                </span>
                <h1 className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-black text-[#FFF9EC] tracking-tight">
                  开通VIP，让您的信息被更多人看见
                </h1>
                <p className="mt-3 text-sm text-[#9A9383]">置顶曝光 · 无限发布 · 专属认证 · 数据分析</p>
              </div>
            </section>

            {/* 权益 */}
            <section className="max-w-5xl mx-auto px-4 lg:px-8 py-10 lg:py-14">
              <h2 className="flex items-center justify-center gap-2 text-xl lg:text-2xl font-black text-[#23201A]">
                <span className="w-1.5 h-6 bg-[#F0A500] rounded-full inline-block" />VIP 专属权益
              </h2>
              <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-5">
                {VIP_BENEFITS.map((b) => (
                  <div key={b.title} className="bg-white rounded-2xl p-5 border border-[#EAE1CB] text-center hover:border-[#F0A500] hover:shadow-lg hover:shadow-[#F0A500]/10 transition-all">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-[#FFF3D6] text-[#B87A00]">
                      <i className={`${b.icon} text-2xl`} />
                    </div>
                    <h3 className="font-bold text-[#23201A] mb-1.5">{b.title}</h3>
                    <p className="text-xs text-[#6E6658] leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* 套餐 */}
            <section className="max-w-4xl mx-auto px-4 lg:px-8 pb-14">
              <h2 className="flex items-center justify-center gap-2 text-xl lg:text-2xl font-black text-[#23201A]">
                <span className="w-1.5 h-6 bg-[#F0A500] rounded-full inline-block" />选择套餐
              </h2>

              {loading ? (
                <div className="text-center py-14">
                  <div className="w-10 h-10 border-[3px] border-[#F0A500] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-sm text-[#8B8577]">套餐加载中…</p>
                </div>
              ) : (
                <>
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {vipPlans.map((plan) => (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`relative cursor-pointer rounded-2xl border-2 p-6 transition-all duration-200 bg-white ${
                          selectedPlan === plan.id
                            ? "border-[#F0A500] shadow-xl shadow-[#F0A500]/15"
                            : "border-[#EAE1CB] hover:border-[#F0A500]/60"
                        }`}
                      >
                        {plan.badge && (
                          <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#23201A] text-[#F0A500] text-[11px] font-bold rounded-full whitespace-nowrap shadow">
                            {plan.badge}
                          </span>
                        )}
                        <div className="text-center mb-4">
                          <h3 className="font-black text-[#23201A] text-base mb-1.5">{plan.name}</h3>
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-3xl font-black text-[#B87A00]">¥{plan.price}</span>
                            <span className="text-sm text-[#8B8577]">/{plan.duration}</span>
                          </div>
                          {plan.originalPrice > plan.price && (
                            <p className="text-xs text-[#9A917E] mt-1 line-through">原价 ¥{plan.originalPrice}</p>
                          )}
                        </div>
                        <ul className="space-y-2 pt-3 border-t border-dashed border-[#EAE1CB]">
                          {plan.features.map((f, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-[#4A453B]">
                              <i className="ri-check-line text-[#F0A500] shrink-0 font-bold" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        {selectedPlan === plan.id && (
                          <span className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-full bg-[#F0A500] text-[#23201A]">
                            <i className="ri-check-line text-sm font-black" />
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* 支付方式 */}
                  <div className="mt-8 bg-white rounded-2xl p-5 border border-[#EAE1CB]">
                    <h3 className="text-sm font-bold text-[#23201A] mb-3 flex items-center gap-1.5">
                      <i className="ri-wallet-3-line text-[#B87A00]" />支付方式
                    </h3>
                    <div className="flex gap-3 flex-wrap">
                      {showQrcode && (
                        <button onClick={() => setPayMethod("qrcode")} className={`${payButtonBase} ${payButtonActive(payMethod === "qrcode")}`}>
                          <i className="ri-qr-code-line text-lg" />收款码支付
                        </button>
                      )}
                      {showWechat && (
                        <button onClick={() => setPayMethod("wechat")} className={`${payButtonBase} ${payButtonActive(payMethod === "wechat")}`}>
                          <i className="ri-wechat-pay-fill text-lg" />微信支付
                        </button>
                      )}
                      {showAlipay && (
                        <button onClick={() => setPayMethod("alipay")} className={`${payButtonBase} ${payButtonActive(payMethod === "alipay")}`}>
                          <i className="ri-alipay-fill text-lg" />支付宝
                        </button>
                      )}
                      {!showQrcode && !showWechat && !showAlipay && (
                        <p className="text-xs text-[#8B8577] py-2">暂无可用的在线支付方式，可先联系管理员开通</p>
                      )}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-7 text-center">
                    <button
                      onClick={handlePay}
                      className="inline-flex items-center gap-2 px-10 py-4 bg-[#F0A500] text-[#23201A] font-black rounded-2xl hover:bg-[#FFB520] transition-colors text-base shadow-xl shadow-[#F0A500]/25 cursor-pointer"
                    >
                      <i className="ri-vip-crown-line text-lg" />
                      立即开通 {currentPlan?.name} — ¥{currentPlan?.price}
                    </button>
                  </div>
                </>
              )}
            </section>
          </>
        )}

        {enableVip && <HoneyFooter />}

        {/* 支付弹窗 */}
        {enableVip && showPayModal && (
          <div className="fixed inset-0 bg-[#23201A]/60 flex items-center justify-center z-[60] px-4">
            <div className="bg-[#FFF9EC] rounded-2xl p-6 w-full max-w-sm border-t-4 border-[#F0A500]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-black text-[#23201A] flex items-center gap-2">
                  <i className="ri-qr-scan-2-line text-[#B87A00]" />扫码支付
                </h3>
                <button
                  onClick={() => setShowPayModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F0A500]/15 text-[#6E6658] transition-colors cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="text-center">
                {payMethod === "alipay" && showAlipay && (
                  <div>
                    <p className="text-xs text-[#6E6658] mb-3">请使用支付宝扫码付款</p>
                    <img src={paymentConfig?.alipay_qrcode} alt="支付宝收款码" className="w-48 h-48 mx-auto rounded-xl border border-[#EAE1CB] bg-white object-contain p-1" />
                  </div>
                )}
                {payMethod === "wechat" && showWechat && (
                  <div>
                    <p className="text-xs text-[#6E6658] mb-3">请使用微信扫码付款</p>
                    <img src={paymentConfig?.wechat_qrcode} alt="微信收款码" className="w-48 h-48 mx-auto rounded-xl border border-[#EAE1CB] bg-white object-contain p-1" />
                  </div>
                )}
                {payMethod === "qrcode" && (
                  <div className="flex flex-col items-center gap-4">
                    {showAlipay && (
                      <div>
                        <p className="text-xs text-[#6E6658] mb-2">支付宝扫码付款</p>
                        <img src={paymentConfig?.alipay_qrcode} alt="支付宝收款码" className="w-40 h-40 mx-auto rounded-xl border border-[#EAE1CB] bg-white object-contain p-1" />
                      </div>
                    )}
                    {showWechat && (
                      <div>
                        <p className="text-xs text-[#6E6658] mb-2">微信扫码付款</p>
                        <img src={paymentConfig?.wechat_qrcode} alt="微信收款码" className="w-40 h-40 mx-auto rounded-xl border border-[#EAE1CB] bg-white object-contain p-1" />
                      </div>
                    )}
                  </div>
                )}
                {!showAlipay && !showWechat && (
                  <>
                    <div className="w-40 h-40 mx-auto mb-3 bg-white border border-[#EAE1CB] rounded-xl flex items-center justify-center">
                      <i className="ri-qr-code-line text-5xl text-[#E4D9BB]" />
                    </div>
                    <p className="text-sm text-[#6E6658] mb-1">支付功能暂未启用</p>
                    <p className="text-xs text-[#9A917E]">请联系站点管理员配置收款方式</p>
                  </>
                )}
                <p className="text-2xl font-black text-[#B87A00] mt-3">¥{currentPlan?.price}</p>
                <p className="text-xs text-[#8B8577]">{currentPlan?.name} · {currentPlan?.duration}</p>
              </div>
              <div className="mt-5 p-3 bg-[#FFF3D6] rounded-xl text-xs text-[#6E5A2A] text-center">
                支付成功后将自动开通VIP权益
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
